/**
 * Puts a newly-opened page at its top.
 *
 * A browser resets the scroll position on a real navigation; a single-page app
 * does not, because nothing navigates — the router swaps the component and the
 * window keeps whatever offset it had. So reaching Privacy from the footer,
 * which you can only do from the bottom of a long page, opened it scrolled
 * most of the way down, with the heading somewhere above the viewport.
 *
 * Honours a #hash, because /account#ai-consent is a real destination: the
 * consent banner links there to explain itself, and jumping to the top of the
 * account page instead would leave the section it promised off-screen.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // The target is rendered in the same commit as this effect, so it is
      // already in the document — but guard anyway and fall back to the top
      // rather than leaving the page wherever it happened to be.
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ block: "start" });
        return;
      }
    }
    // "instant", not smooth: this is a page change, not a movement within a
    // page, and animating it means the reader watches the previous page's
    // content race past before their new one settles.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
}
